import type { FC, KeyboardEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { BackspaceOutlined, SaveOutlined, Send } from '@mui/icons-material'
import { AgentMode, type ClaudeSession } from '@/index'
import {
  CopyButton,
  LongPressIconButton,
  showToast,
  standardTooltipProps,
} from '@/common/components'
import { useEncryptionIndicator } from '@/features/events/shared/hooks'
import { SessionDialog } from './SessionDialog'
import { ExpandControl, useExpandDialog, useZoomLevel, ZoomControls } from '../controls'
import { useSessionStateContext } from '../state'
import type { DraftType, UserAnswersDraft, UserInputDraft } from '../state/types'
import { useUserInputDrafts, useUserAnswersDrafts } from '../drafts'

const PROMPT_CHAR_LIMIT = 20000
const CHAR_WARNING_THRESHOLD = 1000

interface UserInputDialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onSubmit: (prompt: string, agentMode?: AgentMode | null) => Promise<boolean>
  readonly session: ClaudeSession
  readonly isSubmitting: boolean
  readonly error: string | null
  readonly title: string
  readonly placeholder: string
  readonly description: string
  readonly draftType: DraftType
  readonly refEventId: string | null
  readonly currentAgentMode?: AgentMode | null
  readonly agentModeSelectable?: boolean
  readonly dangerouslySkipPermissions?: boolean | undefined
  readonly autoModeSupported?: boolean | undefined
  readonly submitButtonLabel?: string
  readonly submitButtonLoadingLabel?: string
  readonly onDraftSaved?: (() => void) | undefined
  readonly initialValue?: string | undefined
  readonly allowEmptySubmit?: boolean
  readonly deleteOnSubmit?: boolean
  readonly questionHeader?: string | undefined
  readonly eventId?: string | undefined
  readonly hideAgentMode?: boolean
  readonly submitButtonColor?: 'primary' | 'warning' | 'success' | 'error' | 'info'
}

interface CharacterStatusProps {
  readonly currentLength: number
  readonly maxLength: number
  readonly warningThreshold: number
}

interface PromptInstructionsProps {
  readonly description: string
  readonly isEncrypted: boolean
}

interface SubmitButtonProps {
  readonly onSubmit: () => Promise<boolean>
  readonly prompt: string
  readonly isSubmitting: boolean
  readonly isCharLimitExceeded: boolean
  readonly allowEmptySubmit: boolean
  readonly submitButtonLabel: string
  readonly submitButtonLoadingLabel: string
  readonly submitButtonColor: 'primary' | 'warning' | 'success' | 'error' | 'info'
}

export const UserInputDialog: FC<UserInputDialogProps> = ({
  open,
  onClose,
  onSubmit,
  session,
  isSubmitting,
  error,
  title,
  placeholder,
  description,
  draftType,
  refEventId,
  currentAgentMode,
  agentModeSelectable = true,
  dangerouslySkipPermissions,
  autoModeSupported,
  submitButtonLabel = 'Send',
  submitButtonLoadingLabel = 'Sending...',
  allowEmptySubmit = false,
  onDraftSaved,
  initialValue,
  deleteOnSubmit = true,
  questionHeader,
  eventId,
  hideAgentMode = false,
  submitButtonColor = 'primary',
}) => {
  const theme = useTheme()
  const isEncrypted = useEncryptionIndicator()
  const [localError, setLocalError] = useState('')
  const [selectedAgentMode, setSelectedAgentMode] = useState<AgentMode | null>(
    AgentMode.DEFAULT_MODE,
  )
  const [hasUserChangedMode, setHasUserChangedMode] = useState(false)
  const [saveIconFeedback, setSaveIconFeedback] = useState(false)
  const [prompt, setPrompt] = useState('')

  const { updateUserInputDrafts, updateUserAnswersDraft } = useSessionStateContext()
  const hasInitializedRef = useRef(false)
  const prevOpenRef = useRef(open)
  const isDraftSavedRef = useRef(false)

  const isAnswerDraft = draftType === 'custom_answer'
  const isOptionOrAnswerDraft = draftType === 'custom_option' || draftType === 'custom_answer'
  const effectiveAgentModeSelectable = isOptionOrAnswerDraft ? false : agentModeSelectable

  const handleUserInputDraftsChange = useCallback(
    (drafts: UserInputDraft[]) => {
      updateUserInputDrafts(session.sessionId, drafts)
    },
    [updateUserInputDrafts, session.sessionId],
  )

  const handleUserAnswersDraftChange = useCallback(
    (draft: UserAnswersDraft | null) => {
      updateUserAnswersDraft(session.sessionId, draft)
    },
    [updateUserAnswersDraft, session.sessionId],
  )

  const userInputDrafts = useUserInputDrafts({
    drafts: session.runtime.memory.userInputDrafts,
    onUserInputDraftsChange: handleUserInputDraftsChange,
  })

  const effectiveEventId = eventId ?? session.runtime.memory.userAnswersDraft?.eventId ?? ''

  const userAnswersDrafts = useUserAnswersDrafts({
    userAnswersDraft: session.runtime.memory.userAnswersDraft,
    onUserAnswersDraftChange: handleUserAnswersDraftChange,
    eventId: effectiveEventId,
  })

  const existingDraft = useMemo(
    () => userInputDrafts.getDraftByRefEventId(refEventId, draftType),
    [userInputDrafts, refEventId, draftType],
  )

  const existingCustomAnswer = useMemo(
    () =>
      isAnswerDraft && questionHeader ? userAnswersDrafts.getCustomAnswer(questionHeader) : null,
    [isAnswerDraft, questionHeader, userAnswersDrafts],
  )

  useEffect(() => {
    if (prevOpenRef.current && !open) {
      setPrompt('')
      hasInitializedRef.current = false
      setHasUserChangedMode(false)
      isDraftSavedRef.current = false
    }
    prevOpenRef.current = open
  }, [open])

  useEffect(() => {
    if (open) {
      setSelectedAgentMode(currentAgentMode ?? AgentMode.DEFAULT_MODE)
    }
  }, [open, currentAgentMode])

  useEffect(() => {
    if (!open || hasInitializedRef.current) return

    if (initialValue !== undefined) {
      setPrompt(initialValue)
      hasInitializedRef.current = true
      return
    }

    if (isAnswerDraft && existingCustomAnswer) {
      setPrompt(existingCustomAnswer)
      hasInitializedRef.current = true
      return
    }

    if (existingDraft) {
      setPrompt(existingDraft.content)
      hasInitializedRef.current = true
    }
  }, [open, initialValue, existingDraft, isAnswerDraft, existingCustomAnswer])

  const handlePromptChange = useCallback((value: string) => {
    setPrompt(value)
    isDraftSavedRef.current = false
  }, [])

  const handleSaveDraft = useCallback(() => {
    if (!prompt.trim()) return

    if (isAnswerDraft && questionHeader) {
      userAnswersDrafts.saveCustomAnswer(questionHeader, prompt.trim())
    } else {
      userInputDrafts.saveDraft({ refEventId, type: draftType, content: prompt.trim() })
    }

    isDraftSavedRef.current = true
    setSaveIconFeedback(true)
    setTimeout(() => setSaveIconFeedback(false), 1500)
    onDraftSaved?.()
  }, [
    prompt,
    isAnswerDraft,
    questionHeader,
    userAnswersDrafts,
    userInputDrafts,
    refEventId,
    draftType,
    onDraftSaved,
  ])

  const handleDeleteDraft = useCallback(() => {
    if (isAnswerDraft && questionHeader) {
      userAnswersDrafts.deleteCustomAnswer(questionHeader)
    } else if (existingDraft) {
      userInputDrafts.deleteDraft(existingDraft.id)
    }
  }, [isAnswerDraft, questionHeader, userAnswersDrafts, existingDraft, userInputDrafts])

  const isCharLimitExceeded = prompt.length > PROMPT_CHAR_LIMIT

  const handleSubmit = useCallback(
    async (value: string) => {
      if (!allowEmptySubmit && !value.trim()) {
        setLocalError('Please enter a prompt')
        return false
      }

      if (value.length > PROMPT_CHAR_LIMIT) {
        setLocalError(`Character limit exceeded (${value.length}/${PROMPT_CHAR_LIMIT})`)
        return false
      }

      setLocalError('')

      if (isAnswerDraft && questionHeader) {
        userAnswersDrafts.saveCustomAnswer(questionHeader, value.trim())
      }

      const agentModeToSend = hasUserChangedMode ? selectedAgentMode : null
      const success = await onSubmit(value.trim(), agentModeToSend)
      if (success) {
        if (deleteOnSubmit) {
          handleDeleteDraft()
        }
        setPrompt('')
      }
      return success
    },
    [
      allowEmptySubmit,
      onSubmit,
      selectedAgentMode,
      hasUserChangedMode,
      handleDeleteDraft,
      deleteOnSubmit,
      isAnswerDraft,
      questionHeader,
      userAnswersDrafts,
    ],
  )

  const handleClose = useCallback(() => {
    if (prompt.trim()) {
      if (isAnswerDraft && questionHeader) {
        userAnswersDrafts.saveCustomAnswer(questionHeader, prompt.trim())
      } else if (draftType !== 'resume_session') {
        userInputDrafts.saveDraft({ refEventId, type: draftType, content: prompt.trim() })
      }
      if (!isDraftSavedRef.current && draftType !== 'resume_session') {
        showToast('Draft saved', 'info')
      }
      onDraftSaved?.()
    } else if (isAnswerDraft && questionHeader) {
      userAnswersDrafts.deleteCustomAnswer(questionHeader)
    } else if (existingDraft) {
      userInputDrafts.deleteDraft(existingDraft.id)
    }
    onClose()
  }, [
    prompt,
    isAnswerDraft,
    questionHeader,
    userAnswersDrafts,
    userInputDrafts,
    refEventId,
    draftType,
    onDraftSaved,
    existingDraft,
    onClose,
  ])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        handleSubmit(prompt).catch(() => {})
      }
    },
    [handleSubmit, prompt],
  )

  const handleClearPrompt = useCallback(() => {
    setPrompt('')
    handleDeleteDraft()
  }, [handleDeleteDraft])

  const { isExpanded, handleToggleExpand } = useExpandDialog(open)
  const { zoomLevel, fontSize, handleZoomIn, handleZoomOut, handleZoomReset, showZoomLevel } =
    useZoomLevel(open)

  const controlsRowLeft = useMemo(
    () => (
      <ZoomControls
        zoomLevel={zoomLevel}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onZoomReset={handleZoomReset}
        showZoomLevel={showZoomLevel}
      />
    ),
    [zoomLevel, handleZoomIn, handleZoomOut, handleZoomReset, showZoomLevel],
  )

  const controlsRowCenter = useMemo(
    () => (
      <>
        <CopyButton text={prompt} variant="controls" tooltipText="Copy prompt" />
        <Tooltip {...standardTooltipProps} title="Save draft">
          <IconButton
            size="small"
            onClick={handleSaveDraft}
            disabled={!prompt.trim()}
            sx={saveIconButtonSx(saveIconFeedback)}
          >
            <SaveOutlined sx={controlIconSx} />
          </IconButton>
        </Tooltip>
        <LongPressIconButton
          icon={<BackspaceOutlined sx={controlIconSx} />}
          onLongPress={handleClearPrompt}
          tooltipText="Hold to clear"
          duration={1000}
          progressColor={theme.palette.error.main}
          activeColor={prompt.trim() ? 'error.main' : undefined}
          disabled={!prompt.trim()}
        />
      </>
    ),
    [handleSaveDraft, prompt, saveIconFeedback, handleClearPrompt, theme.palette.error.main],
  )

  const windowControlSlot3 = useMemo(
    () => <ExpandControl isExpanded={isExpanded} onToggle={handleToggleExpand} />,
    [isExpanded, handleToggleExpand],
  )

  const displayError = localError || error

  return (
    <SessionDialog
      open={open}
      onClose={isSubmitting ? () => {} : handleClose}
      title={title}
      breadcrumb={session.sessionBreadcrumb}
      agentMode={hideAgentMode ? null : selectedAgentMode}
      agentModeSelectable={effectiveAgentModeSelectable}
      onAgentModeChange={(mode) => {
        setSelectedAgentMode(mode)
        setHasUserChangedMode(true)
      }}
      dangerouslySkipPermissions={dangerouslySkipPermissions}
      autoModeSupported={autoModeSupported}
      windowControlSlot3={windowControlSlot3}
      controlsRowLeft={controlsRowLeft}
      controlsRowCenter={controlsRowCenter}
      isExpanded={isExpanded}
      fontSize={fontSize}
    >
      <Box sx={formContainerSx}>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={20}
          placeholder={placeholder}
          value={prompt}
          onChange={(e) => handlePromptChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSubmitting}
          error={!!displayError}
          helperText={displayError}
          sx={textFieldSx(fontSize)}
        />

        <CharacterStatus
          currentLength={prompt.length}
          maxLength={PROMPT_CHAR_LIMIT}
          warningThreshold={CHAR_WARNING_THRESHOLD}
        />

        <PromptInstructions description={description} isEncrypted={isEncrypted} />

        <SubmitButton
          onSubmit={() => handleSubmit(prompt)}
          prompt={prompt}
          isSubmitting={isSubmitting}
          isCharLimitExceeded={isCharLimitExceeded}
          allowEmptySubmit={allowEmptySubmit}
          submitButtonLabel={submitButtonLabel}
          submitButtonLoadingLabel={submitButtonLoadingLabel}
          submitButtonColor={submitButtonColor}
        />
      </Box>
    </SessionDialog>
  )
}

const CharacterStatus: FC<CharacterStatusProps> = ({
  currentLength,
  maxLength,
  warningThreshold,
}) => {
  const remaining = maxLength - currentLength
  const isExceeded = remaining < 0
  const isWarning = remaining <= warningThreshold && remaining >= 0

  if (!isWarning && !isExceeded) return null

  const text = isExceeded
    ? `Character limit exceeded (${currentLength}/${maxLength})`
    : `${remaining} characters remaining`

  return (
    <Typography variant="caption" sx={characterStatusSx(isExceeded)}>
      {text}
    </Typography>
  )
}

const PromptInstructions: FC<PromptInstructionsProps> = ({ description, isEncrypted }) => (
  <Box sx={instructionsContainerSx}>
    <Typography variant="caption" color="text.secondary">
      Press{' '}
      <Typography component="span" variant="caption" sx={keyboardShortcutSx}>
        Cmd/Ctrl+Enter
      </Typography>{' '}
      to send
    </Typography>

    <Typography variant="caption" color="text.secondary" sx={descriptionTextSx}>
      {description}
    </Typography>

    {isEncrypted && (
      <Typography variant="caption" role="status" aria-live="polite" sx={encryptionIndicatorSx}>
        All outbound messages are end-to-end encrypted.
      </Typography>
    )}
  </Box>
)

const SubmitButton: FC<SubmitButtonProps> = ({
  onSubmit,
  prompt,
  isSubmitting,
  isCharLimitExceeded,
  allowEmptySubmit,
  submitButtonLabel,
  submitButtonLoadingLabel,
  submitButtonColor,
}) => (
  <Box sx={submitButtonContainerSx}>
    <Button
      onClick={() => onSubmit().catch(() => {})}
      variant="contained"
      color={submitButtonColor}
      disabled={isSubmitting || (!allowEmptySubmit && !prompt.trim()) || isCharLimitExceeded}
      startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
    >
      {isSubmitting ? submitButtonLoadingLabel : submitButtonLabel}
    </Button>
  </Box>
)

const formContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}

const instructionsContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
}

const keyboardShortcutSx: SxProps<Theme> = {
  color: 'primary.main',
  fontWeight: 600,
}

const descriptionTextSx: SxProps<Theme> = {
  whiteSpace: 'pre-line',
}

const encryptionIndicatorSx: SxProps<Theme> = {
  color: 'success.main',
}

const submitButtonContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
}

const textFieldSx = (fontSize: string): SxProps<Theme> => ({
  '& .MuiInputBase-input': {
    fontSize,
  },
})

const controlIconSx: SxProps<Theme> = {
  fontSize: '1.1rem',
}

const saveIconButtonSx = (showFeedback: boolean): SxProps<Theme> => ({
  color: showFeedback ? 'success.main' : 'primary.main',
  transition: 'color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'transparent',
    color: showFeedback ? 'success.main' : 'primary.light',
  },
  '&.Mui-disabled': {
    color: 'action.disabled',
  },
})

const characterStatusSx = (isExceeded: boolean): SxProps<Theme> => ({
  color: isExceeded ? 'error.main' : 'warning.main',
  opacity: isExceeded ? 1 : 0.7,
  fontSize: '0.75rem',
  fontWeight: 600,
  mt: -1,
  textAlign: 'right',
})
